/**
 * Team sign-off neutral template — must match functions/inHouseSigning/signingSheetConfig.js templateStoragePathNeutral
 */
export const NEUTRAL_SIGNING_TEMPLATE_STORAGE_PATH = 'global/crewchain_blank.pdf';

/** Stored in Firestore as branding.teamSignOffSheetStyle — must match functions resolver + updateCompanyBranding */
export const TEAM_SIGN_OFF_SHEET_STYLES = Object.freeze(['blank', 'branded']);

/**
 * Explicit saved preference, or null if the org uses legacy rules (Pro + logo → branded sheet).
 * @param {object | null | undefined} company
 * @returns {'crewchain' | 'blank' | 'branded' | null}
 */
export function getTeamSignOffSheetStyleFromCompany(company) {
  const raw = company?.branding?.teamSignOffSheetStyle;
  if (raw == null || raw === '') return null;
  const s = String(raw).toLowerCase();
  return TEAM_SIGN_OFF_SHEET_STYLES.includes(s) ? s : null;
}

/**
 * Effective style including legacy when teamSignOffSheetStyle is unset (matches Cloud Function resolver).
 * @param {object | null | undefined} company
 * @returns {'crewchain' | 'blank' | 'branded'}
 */
export function effectiveTeamSignOffSheetStyle(company) {
  const planId = String(company?.planId || '').toLowerCase();
  if (planId !== 'pro' && planId !== 'proplus') {
    return 'blank';
  }
  const explicit = getTeamSignOffSheetStyleFromCompany(company);
  if (explicit) return explicit;
  const logoUrl = company?.branding?.logoUrl;
  const u = typeof logoUrl === 'string' ? logoUrl.trim() : '';
  const hasLogo = u && (u.startsWith('https://') || u.startsWith('http://'));
  return hasLogo ? 'branded' : 'blank';
}

/** A4 pt — must match signingSheetConfig.page */
export const SIGNING_SHEET_PAGE_PT = { width: 595.28, height: 841.89 };

/** Must match functions/inHouseSigning/signingSheetConfig.js */
export const SIGNING_SHEET_TABLE_GEOM = {
  firstRowY: 700.19,
  rowHeight: 55.85,
  rows: 12,
  marginLeft: 20,
  marginRight: 20,
  marginBottom: 30,
  marginTop: 30,
  gapBelowTablePt: 16,
  pageW: SIGNING_SHEET_PAGE_PT.width,
};

/** Bottom of lowest signing row (PDF pt, bottom-left origin). */
export function getSigningSheetLowestRowBottomYPt() {
  const { firstRowY, rowHeight, rows } = SIGNING_SHEET_TABLE_GEOM;
  return firstRowY - (rows - 1) * rowHeight;
}

/** Max PDF y for the top edge of the logo (must sit below grid + gap). */
export function getSigningSheetLogoTopMaxYPt() {
  return getSigningSheetLowestRowBottomYPt() - SIGNING_SHEET_TABLE_GEOM.gapBelowTablePt;
}

/**
 * Keep logo box in the bottom margin strip only — below all sign-in rows (not over fields or header text).
 * @param {{ xPt: number; yPt: number; widthPt: number }} layout
 * @param {number} aspectRatio width / height
 */
export function clampSigningSheetLogoLayout(layout, aspectRatio) {
  const aspect = aspectRatio > 0 ? aspectRatio : 1;
  const { marginLeft, marginRight, marginBottom, pageW } = SIGNING_SHEET_TABLE_GEOM;
  const minYPt = marginBottom;
  const logoTopMaxYPt = getSigningSheetLogoTopMaxYPt();
  const maxH = logoTopMaxYPt - minYPt;

  let widthPt = Math.min(220, Math.max(20, layout.widthPt));
  let hPt = widthPt / aspect;
  if (hPt > maxH) {
    hPt = maxH;
    widthPt = Math.max(20, hPt * aspect);
    hPt = widthPt / aspect;
  }
  if (hPt > maxH) {
    widthPt = maxH * aspect;
    hPt = maxH;
  }

  const minXPt = marginLeft;
  const maxXPt = pageW - marginRight - widthPt;
  const maxYPt = logoTopMaxYPt - hPt;

  let xPt = layout.xPt;
  let yPt = layout.yPt;
  if (maxXPt >= minXPt) {
    xPt = Math.min(Math.max(xPt, minXPt), maxXPt);
  } else {
    xPt = minXPt;
    widthPt = Math.min(widthPt, pageW - marginLeft - marginRight);
    hPt = widthPt / aspect;
    if (hPt > maxH) {
      widthPt = maxH * aspect;
      hPt = maxH;
    }
  }
  if (maxYPt >= minYPt) {
    yPt = Math.min(Math.max(yPt, minYPt), maxYPt);
  } else {
    yPt = minYPt;
  }

  return { xPt, yPt, widthPt };
}

/**
 * Default team sign-off logo: bottom-right below the grid (pdf-lib bottom-left origin).
 * Width uses min(maxWidth, maxHeight strip) for a square reference — matches server when image is square.
 */
export function getDefaultSigningSheetLogoLayout() {
  const pageW = SIGNING_SHEET_PAGE_PT.width;
  const maxWidthPt = 72;
  const rightMargin = 36;
  const yMin = SIGNING_SHEET_TABLE_GEOM.marginBottom;
  const logoTopMaxY = getSigningSheetLogoTopMaxYPt();
  const maxH = logoTopMaxY - yMin;
  const widthPt = Math.min(maxWidthPt, Math.max(20, maxH));
  const xPt = pageW - rightMargin - widthPt;
  const yPt = yMin;
  return { xPt, yPt, widthPt };
}

/**
 * @param {object | null | undefined} company
 * @returns {{ xPt: number; yPt: number; widthPt: number } | null}
 */
export function parseSigningSheetLogoLayoutFromCompany(company) {
  const L = company?.branding?.signingSheetLogoLayout;
  if (!L || typeof L !== 'object') return null;
  const xPt = Number(L.xPt);
  const yPt = Number(L.yPt);
  const widthPt = Number(L.widthPt);
  if (!Number.isFinite(xPt) || !Number.isFinite(yPt) || !Number.isFinite(widthPt)) return null;
  if (widthPt < 20 || widthPt > 220) return null;
  if (xPt < -40 || xPt > 620 || yPt < -40 || yPt > 900) return null;
  return { xPt, yPt, widthPt };
}
